// ============================================================================
// CLOSURE REASON FORM MODAL — Add / Edit Closure Reason (MSTR-CLRN-001)
// ============================================================================
// Adds outcome_category enum SyutSelect to the standard A-tier shape.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { closureReasonService } from 'api/services/closureReasonService';
import type { ClosureReason, OutcomeCategory } from 'api/types/closureReason.types';

type ClosureStatus = 'ACTIVE' | 'INACTIVE';

interface ClosureReasonFormData {
  closureCode: string;
  closureName: string;
  outcomeCategory: OutcomeCategory;
  statusCode: ClosureStatus;
}

const INITIAL_FORM_DATA: ClosureReasonFormData = {
  closureCode: '',
  closureName: '',
  outcomeCategory: 'NEUTRAL',
  statusCode: 'ACTIVE',
};

const OUTCOME_OPTIONS = [
  { value: 'FAVOURABLE', label: 'Favourable' },
  { value: 'UNFAVOURABLE', label: 'Unfavourable' },
  { value: 'NEUTRAL', label: 'Neutral' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface ClosureReasonFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: ClosureReason | null;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const ClosureReasonFormModal: React.FC<ClosureReasonFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<ClosureReasonFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: ClosureReasonFormData = rec
      ? {
          closureCode: rec.closureCode,
          closureName: rec.closureName,
          outcomeCategory: rec.outcomeCategory,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as ClosureStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.closureCode || currentFormData.closureCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.closureCode?.trim();
      const name = currentFormData.closureName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.closureCode || currentRecord?.closureCode || 'Closure Reason'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.closureCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.closureCode.trim();
    const name = formData.closureName.trim();

    if (!code) errors.closureCode = 'Closure code is required';
    else if (code.length > 30) errors.closureCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.closureCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.closureName = 'Closure name is required';
    else if (name.length > 100) errors.closureName = 'Max 100 characters';

    if (!formData.outcomeCategory) errors.outcomeCategory = 'Outcome category is required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.closureCode.trim().toUpperCase();
      const name = formData.closureName.trim();

      if (mode === 'create' || mode === 'copy') {
        await closureReasonService.create({
          closureCode: code,
          closureName: name,
          outcomeCategory: formData.outcomeCategory,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Closure Reason "${code}" created successfully.`);
      } else if (record) {
        await closureReasonService.update(record.id, {
          closureCode: code,
          closureName: name,
          outcomeCategory: formData.outcomeCategory,
          statusCode: formData.statusCode,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Closure Reason "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This closure reason was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A closure reason with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save closure reason. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'view' ? 'View Closure Reason' : mode === 'copy' ? 'Copy Closure Reason' : mode === 'create' ? 'Add Closure Reason' : 'Edit Closure Reason'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-group" data-field="closureCode">
            <label className="form-label">
              Closure Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('closureCode') ? ' form-field-error' : ''}`}
              value={formData.closureCode}
              maxLength={30}
              placeholder="SETTLED, WRITE-OFF..."
              onChange={(e) => {
                clearFieldError('closureCode');
                setFormData(prev => ({ ...prev, closureCode: e.target.value.toUpperCase() }));
              }}
              disabled={isSaving}
            />
            {getFieldError('closureCode') && (
              <div className="form-error-message">{getFieldError('closureCode')}</div>
            )}
          </div>

          <div className="form-group" data-field="closureName">
            <label className="form-label">
              Closure Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('closureName') ? ' form-field-error' : ''}`}
              value={formData.closureName}
              maxLength={100}
              placeholder="Settled Out of Court"
              onChange={(e) => {
                clearFieldError('closureName');
                setFormData(prev => ({ ...prev, closureName: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('closureName') && (
              <div className="form-error-message">{getFieldError('closureName')}</div>
            )}
          </div>

          <div className="form-group" data-field="outcomeCategory">
            <SyutSelect
              label={<>Outcome Category <span className="text-danger">*</span></>}
              required
              options={OUTCOME_OPTIONS}
              value={formData.outcomeCategory}
              onChange={(val) => {
                clearFieldError('outcomeCategory');
                setFormData(prev => ({
                  ...prev,
                  outcomeCategory: ((val as string) || 'NEUTRAL') as OutcomeCategory,
                }));
              }}
              error={getFieldError('outcomeCategory')}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(val) => setFormData(prev => ({
                ...prev,
                statusCode: ((val as string) || 'ACTIVE') as ClosureStatus,
              }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          (mode === 'create' || mode === 'copy')
            ? `Create closure reason "${formData.closureCode.trim().toUpperCase()}"?`
            : `Update closure reason "${formData.closureCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default ClosureReasonFormModal;
